/**
 * The body of the "Held-out tests" dialog (TRA-1434): the tests of the
 * team's latest scoring night with a tick on each one that is held out.
 *
 * It holds the selection and nothing else. The dialog around it reads and
 * saves, and remounts this by the saved tests' revision, so the ticks
 * always start from what is saved.
 */

import { useId, useState } from 'react'

import type { HeldOutTestsResponse } from '@/api/generated/model'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DialogFooter } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'

import {
  isChanged,
  savedSelection,
  scoredHeldOut,
  splitPath,
  toggled,
  withAllScored,
} from './heldOutTests'

interface HeldOutTestsFormProps {
  data: HeldOutTestsResponse
  isPending: boolean
  onCancel: () => void
  onSave: (sourceModules: string[]) => void
}

const EYEBROW = 'font-mono text-[10px] uppercase tracking-wider text-muted-foreground'
const CHIP = 'h-6 rounded-sm px-2.5 font-mono text-[10px] uppercase tracking-wider'

function TestRow({
  module,
  taskCount,
  checked,
  disabled,
  onToggle,
}: {
  module: string
  /** Shown when one test file produced several tasks. */
  taskCount: number
  checked: boolean
  disabled: boolean
  onToggle: (module: string) => void
}): React.JSX.Element {
  const id = useId()
  const { folder, name } = splitPath(module)
  return (
    <li className="flex items-center gap-3 border-b border-border/50 px-3 py-2 last:border-b-0">
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={() => { onToggle(module) }}
      />
      {/* The file name is what tells one test from the next, so it leads;
          the folder sits under it. The whole path is what gets saved. */}
      <Label
        htmlFor={id}
        title={module}
        className="min-w-0 flex-1 cursor-pointer font-mono text-xs font-normal leading-5"
      >
        <span className="block truncate">{name}</span>
        <span className="block truncate text-[11px] text-muted-foreground">{folder}</span>
      </Label>
      {taskCount > 1 && (
        <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
          {taskCount} tasks
        </span>
      )}
    </li>
  )
}

/** A team no night has scored: nothing to pick from, and what is saved. */
function NoNight({
  data,
  onCancel,
}: Pick<HeldOutTestsFormProps, 'data' | 'onCancel'>): React.JSX.Element {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{data.no_night_reason}</p>
      {data.not_in_night.length > 0 && (
        <div className="space-y-2">
          <p className={EYEBROW}>Saved tests</p>
          <ul className="max-h-[40vh] overflow-y-auto border border-border px-3 py-2 font-mono text-xs leading-6">
            {data.not_in_night.map((module) => (
              <li key={module} className="break-all">
                {module}
              </li>
            ))}
          </ul>
        </div>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Close
        </Button>
      </DialogFooter>
    </div>
  )
}

export function HeldOutTestsForm({
  data,
  isPending,
  onCancel,
  onSave,
}: HeldOutTestsFormProps): React.JSX.Element {
  const [selected, setSelected] = useState<ReadonlySet<string>>(
    () => new Set(savedSelection(data)),
  )
  if (data.night === null) return <NoNight data={data} onCancel={onCancel} />

  const toggle = (module: string): void => {
    setSelected((current) => toggled(current, module))
  }
  const empty = selected.size === 0

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault()
        if (!empty) onSave([...selected])
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className={EYEBROW}>
          {scoredHeldOut(selected, data)} of {data.tests.length} held out
        </p>
        <div className="flex gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={CHIP}
            disabled={isPending}
            onClick={() => { setSelected((current) => withAllScored(current, data)) }}
          >
            Select all
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={CHIP}
            disabled={isPending}
            onClick={() => { setSelected(new Set()) }}
          >
            Clear
          </Button>
        </div>
      </div>
      <ul
        aria-label="Tests of the latest scoring night"
        className="max-h-[45vh] overflow-y-auto border border-border"
      >
        {data.tests.map((test) => (
          <TestRow
            key={test.source_module}
            module={test.source_module}
            taskCount={test.task_count}
            checked={selected.has(test.source_module)}
            disabled={isPending}
            onToggle={toggle}
          />
        ))}
      </ul>
      {data.not_in_night.length > 0 && (
        <div className="space-y-2">
          <p className={EYEBROW}>Not in the latest night</p>
          <p className="text-xs text-muted-foreground">
            The latest night did not score these saved tests. The dataset build fails on a test it
            cannot find.
          </p>
          <ul aria-label="Saved tests the latest night did not score" className="border border-border">
            {data.not_in_night.map((module) => (
              <TestRow
                key={module}
                module={module}
                taskCount={1}
                checked={selected.has(module)}
                disabled={isPending}
                onToggle={toggle}
              />
            ))}
          </ul>
        </div>
      )}
      {empty && (
        <p className="text-sm text-destructive" role="alert">
          Pick at least one test.
        </p>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={isPending || empty || !isChanged(selected, data)}>
          {isPending ? 'Saving...' : 'Save held-out tests'}
        </Button>
      </DialogFooter>
    </form>
  )
}
